import { createClient } from '@supabase/supabase-js'
import { log } from '../utils/logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env.local file.')
}

// Enhanced Supabase client configuration with better session management
// NOTE: Removed flowType: 'pkce' to allow password reset emails to work across browsers
// PKCE requires the code verifier to be in the same browser's localStorage,
// which breaks password reset when users open the link in a different browser
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'supabase.auth.token'
    // flowType: 'pkce' - Removed to support cross-browser password reset
  },
  global: {
    headers: {
      'X-Client-Info': 'teamrezy-web-app'
    }
  }
})

// Database helper functions
export const db = {
  // Game Locations
  async getGameLocations(filters = {}) {
    let query = supabase
      .from('game_locations')
      .select('*')

    // Only filter by active status if not explicitly requesting all
    if (filters.includeInactive !== true) {
      query = query.eq('is_active', true)
    }

    if (filters.city) {
      query = query.ilike('city', `%${filters.city}%`)
    }
    if (filters.state) {
      query = query.ilike('state', `%${filters.state}%`)
    }

    return query.order('name')
  },

  async getGameLocationById(id) {
    return supabase
      .from('game_locations')
      .select('*')
      .eq('id', id)
      .single()
  },
  
  async createGameLocation(locationData) {
    // Validate required fields
    if (!locationData || !locationData.name) {
      throw new Error('Location name is required');
    }

    // Clean up the data before sending to database
    const cleanData = { ...locationData };
    
    // Convert ALL empty strings to null for proper database handling
    Object.keys(cleanData).forEach(key => {
      if (cleanData[key] === '') {
        cleanData[key] = null;
      }
    });
    
    // Convert numeric fields specifically
    if (cleanData.latitude !== null && cleanData.latitude !== undefined) {
      if (typeof cleanData.latitude === 'string' && cleanData.latitude.trim() !== '') {
        cleanData.latitude = parseFloat(cleanData.latitude);
      } else if (typeof cleanData.latitude !== 'number') {
        cleanData.latitude = null;
      }
    }
    
    if (cleanData.longitude !== null && cleanData.longitude !== undefined) {
      if (typeof cleanData.longitude === 'string' && cleanData.longitude.trim() !== '') {
        cleanData.longitude = parseFloat(cleanData.longitude);
      } else if (typeof cleanData.longitude !== 'number') {
        cleanData.longitude = null;
      }
    }
    
    // Debug logging - check ALL fields for empty strings
    log.info('createGameLocation - cleanData being sent to database:', cleanData);
    Object.keys(cleanData).forEach(key => {
      const value = cleanData[key];
      if (value === '') {
        log.warn(`⚠️  Empty string found in field '${key}':`, value);
      }
      log.info(`${key}:`, value, 'Type:', typeof value);
    });
    
    log.info('About to call supabase.from...');
    
    const result = await supabase
      .from('game_locations')
      .insert([cleanData])
      .select()
      .single();
    
    log.info('Supabase result:', result);
    return result;
  },

  async updateGameLocation(id, updates) {
    // Clean up the data before sending to database
    const cleanUpdates = { ...updates };
    
    // Convert ALL empty strings to null for proper database handling
    Object.keys(cleanUpdates).forEach(key => {
      if (cleanUpdates[key] === '') {
        cleanUpdates[key] = null;
      }
    });
    
    // Convert numeric fields specifically
    if (cleanUpdates.latitude !== null && cleanUpdates.latitude !== undefined) {
      if (typeof cleanUpdates.latitude === 'string' && cleanUpdates.latitude.trim() !== '') {
        cleanUpdates.latitude = parseFloat(cleanUpdates.latitude);
      } else if (typeof cleanUpdates.latitude !== 'number') {
        cleanUpdates.latitude = null;
      }
    }
    
    if (cleanUpdates.longitude !== null && cleanUpdates.longitude !== undefined) {
      if (typeof cleanUpdates.longitude === 'string' && cleanUpdates.longitude.trim() !== '') {
        cleanUpdates.longitude = parseFloat(cleanUpdates.longitude);
      } else if (typeof cleanUpdates.longitude !== 'number') {
        cleanUpdates.longitude = null;
      }
    }
    
    // Debug logging
    log.info('updateGameLocation - cleanUpdates being sent to database:', cleanUpdates);
    log.info('Latitude:', cleanUpdates.latitude, 'Type:', typeof cleanUpdates.latitude);
    log.info('Longitude:', cleanUpdates.longitude, 'Type:', typeof cleanUpdates.longitude);
    
    return supabase
      .from('game_locations')
      .update(cleanUpdates)
      .eq('id', id)
      .select()
      .single()
  },
  
  async deleteGameLocation(id) {
    // Soft delete by setting is_active to false
    return supabase
      .from('game_locations')
      .update({ is_active: false })
      .eq('id', id)
      .select()
      .single()
  },

  // Tournaments
  async getTournaments(filters = {}) {
    let query = supabase
      .from('tournaments')
      .select('*')
      .eq('is_active', true)

    if (filters.hostCity) {
      query = query.ilike('host_city', `%${filters.hostCity}%`)
    }
    if (filters.hostState) {
      query = query.ilike('host_state', `%${filters.hostState}%`)
    }
    if (filters.startDate) {
      query = query.gte('start_date', filters.startDate)
    }
    if (filters.endDate) {
      query = query.lte('end_date', filters.endDate)
    }

    return query.order('start_date', { ascending: true })
  },

  async getTournamentById(id) {
    return supabase
      .from('tournaments')
      .select('*')
      .eq('id', id)
      .single()
  },
  
  async createTournament(tournamentData) {
    return supabase
      .from('tournaments')
      .insert([tournamentData])
      .select()
      .single()
  },

  async updateTournament(id, updates) {
    return supabase
      .from('tournaments')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
  },
  
  async deleteTournament(id) {
    // Soft delete by setting is_active to false
    return supabase
      .from('tournaments')
      .update({ is_active: false })
      .eq('id', id)
      .select()
      .single()
  },

  // Tournament Locations functions
  async getTournamentLocations(tournamentId) {
    return supabase
      .from('tournament_locations')
      .select('*, game_locations(id, name, address_line1, city, state, latitude, longitude)')
      .eq('tournament_id', tournamentId)
      .order('is_main_location', { ascending: false }) // Main location first
  },

  async createTournamentLocations(tournamentId, locationIds, mainLocationId = null) {
    if (!locationIds || locationIds.length === 0) {
      return { data: [], error: null };
    }

    // Set the first location as main if no main location specified
    const actualMainLocationId = mainLocationId || locationIds[0];

    const tournamentLocations = locationIds.map((locationId, _index) => ({
      tournament_id: tournamentId,
      location_id: locationId,
      is_main_location: locationId === actualMainLocationId
    }));

    return supabase
      .from('tournament_locations')
      .insert(tournamentLocations)
      .select()
  },

  async updateTournamentLocations(tournamentId, locationIds, mainLocationId = null) {
    // Delete existing tournament locations
    await supabase
      .from('tournament_locations')
      .delete()
      .eq('tournament_id', tournamentId);

    // Insert new tournament locations
    if (locationIds && locationIds.length > 0) {
      return this.createTournamentLocations(tournamentId, locationIds, mainLocationId);
    }

    return { data: [], error: null };
  },

  async setMainTournamentLocation(tournamentId, locationId) {
    // First, unset all main locations for this tournament
    await supabase
      .from('tournament_locations')
      .update({ is_main_location: false })
      .eq('tournament_id', tournamentId);

    // Then set the specified location as main
    return supabase
      .from('tournament_locations')
      .update({ is_main_location: true })
      .eq('tournament_id', tournamentId)
      .eq('location_id', locationId)
      .select()
  },

  async getMainTournamentLocation(tournamentId) {
    return supabase
      .from('tournament_locations')
      .select('*, game_locations(id, name, address_line1, city, state, latitude, longitude)')
      .eq('tournament_id', tournamentId)
      .eq('is_main_location', true)
      .single()
  },

  async deleteTournamentLocation(tournamentId, locationId) {
    return supabase
      .from('tournament_locations')
      .delete()
      .eq('tournament_id', tournamentId)
      .eq('location_id', locationId)
  },

  // Team Manager functions
  async getTeamManagerTournaments(filters = {}) {
    let query = supabase
      .from('team_manager_tournaments')
      .select('*');

    if (filters.searchTerm) {
      query = query.or(`name.ilike.%${filters.searchTerm}%,host_city.ilike.%${filters.searchTerm}%,sport.ilike.%${filters.searchTerm}%`);
    }

    return query.order('start_date', { ascending: true });
  },

  async getUserManagedTeams(userId) {
    return supabase
      .rpc('get_user_managed_teams', { p_user_id: userId });
  },

  async canUserManageTeam(userId, teamId) {
    return supabase
      .rpc('can_user_manage_team', { 
        user_id: userId, 
        team_id: teamId 
      });
  },

  async addTeamToTournament(tournamentId, teamId, userId) {
    // Check if user can manage the team
    const { data: canManage, error: checkError } = await this.canUserManageTeam(userId, teamId);
    if (checkError) throw checkError;
    if (!canManage) {
      throw new Error('You do not have permission to manage this team');
    }

    return supabase
      .from('tournament_teams')
      .insert({
        tournament_id: tournamentId,
        team_id: teamId,
        created_by: userId
      })
      .select()
      .single();
  },

  async removeTeamFromTournament(tournamentId, teamId, userId) {
    // Check if user can manage the team
    const { data: canManage, error: checkError } = await this.canUserManageTeam(userId, teamId);
    if (checkError) throw checkError;
    if (!canManage) {
      throw new Error('You do not have permission to manage this team');
    }

    return supabase
      .from('tournament_teams')
      .delete()
      .eq('tournament_id', tournamentId)
      .eq('team_id', teamId);
  },

  async getTeamTournaments(teamId) {
    return supabase
      .from('tournament_teams_view')
      .select('*')
      .eq('team_id', teamId);
  },

  async createGameAsTeamManager(gameData, _userId) {
    // The database trigger will validate that the user can manage at least one of the teams
    return supabase
      .from('games')
      .insert([gameData])
      .select(`
        *,
        tournaments (name, host_city, host_state, timezone),
        game_locations (id, name, city, state, latitude, longitude),
        home_team:teams!home_team_id (name),
        away_team:teams!away_team_id (name)
      `)
      .single();
  },

  async updateGameAsTeamManager(gameId, updates, _userId) {
    // The database trigger will validate that the user can manage at least one of the teams
    return supabase
      .from('games')
      .update(updates)
      .eq('id', gameId)
      .select(`
        *,
        tournaments (name, host_city, host_state, timezone),
        game_locations (id, name, city, state, latitude, longitude),
        home_team:teams!home_team_id (name),
        away_team:teams!away_team_id (name)
      `)
      .single();
  },

  async getGameChanges(gameId) {
    return supabase
      .from('game_changes')
      .select(`
        *,
        games (
          tournament_id,
          home_team:teams!home_team_id (name),
          away_team:teams!away_team_id (name)
        )
      `)
      .eq('game_id', gameId)
      .order('created_at', { ascending: false });
  },

  async getTeamManagerGames(userId, filters = {}) {
    // Get teams managed by user
    const { data: managedTeams, error: teamsError } = await this.getUserManagedTeams(userId);
    if (teamsError) throw teamsError;
    
    if (!managedTeams || managedTeams.length === 0) {
      return { data: [], error: null };
    }

    const teamIds = managedTeams.map(t => t.team_id);
    
    let query = supabase
      .from('games')
      .select(`
        *,
        tournaments (name, host_city, host_state, timezone),
        game_locations (id, name, city, state, latitude, longitude),
        home_team:teams!home_team_id (name),
        away_team:teams!away_team_id (name)
      `)
      .or(`home_team_id.in.(${teamIds.join(',')}),away_team_id.in.(${teamIds.join(',')})`);

    if (filters.tournamentId) {
      query = query.eq('tournament_id', filters.tournamentId);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    return query.order('starts_at', { ascending: true });
  },

  // Games
  async getGames(filters = {}) {
    let query = supabase
      .from('games')
      .select(`
        *,
        tournaments (
          name,
          host_city,
          host_state,
          timezone
        ),
        game_locations (
          id,
          name,
          address_line1,
          city,
          state,
          phone,
          latitude,
          longitude
        ),
        home_team:teams!home_team_id (
          name
        ),
        away_team:teams!away_team_id (
          name
        )
      `)

    if (filters.tournamentId) {
      query = query.eq('tournament_id', filters.tournamentId)
    }
    if (filters.locationId) {
      query = query.eq('location_id', filters.locationId)
    }
    if (filters.teamId) {
      query = query.or(`home_team_id.eq.${filters.teamId},away_team_id.eq.${filters.teamId}`)
    }
    if (filters.sport) {
      query = query.ilike('sport', `%${filters.sport}%`)
    }
    if (filters.startDate) {
      query = query.gte('starts_at', filters.startDate)
    }
    if (filters.endDate) {
      query = query.lte('starts_at', filters.endDate)
    }
    if (filters.status) {
      query = query.eq('status', filters.status)
    }

    return query.order('starts_at', { ascending: true })
  },

  async getGameById(id) {
    return supabase
      .from('games')
      .select(`
        *,
        tournaments (
          name,
          host_city,
          host_state,
          timezone
        ),
        game_locations (
          id,
          name,
          address_line1,
          city,
          state,
          phone,
          latitude,
          longitude
        ),
        home_team:teams!home_team_id (
          name
        ),
        away_team:teams!away_team_id (
          name
        )
      `)
      .eq('id', id)
      .single()
  },
  
  async createGame(gameData) {
    return supabase
      .from('games')
      .insert([gameData])
      .select()
      .single()
  },

  async updateGame(id, updates) {
    return supabase
      .from('games')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
  },
  
  async updateGameScore(id, homeScore, awayScore) {
    return supabase
      .from('games')
      .update({ 
        score_home: homeScore, 
        score_away: awayScore,
        status: 'completed'
      })
      .eq('id', id)
      .select()
      .single()
  },
  
  async updateGameStatus(id, status) {
    return supabase
      .from('games')
      .update({ status })
      .eq('id', id)
      .select()
      .single()
  },
  
  async deleteGame(id) {
    return supabase
      .from('games')
      .delete()
      .eq('id', id)
  },
  // Users
  async getAllUsers() {
    // Use the SECURITY DEFINER function for teamrezy_employees to see all users
    const { data, error } = await supabase.rpc('teamrezy_get_all_users');
    return { data, error };
  },
  
  async getAllUserTeamRoles() {
    // Use the SECURITY DEFINER function for teamrezy_employees to see all user team roles
    const { data, error } = await supabase.rpc('teamrezy_get_all_user_team_roles');
    return { data, error };
  },
  
  async deactivateUser(userId) {
    // First get the user's email
    const { data: userData, error: fetchError } = await supabase
      .from('users')
      .select('email')
      .eq('id', userId)
      .single();
      
    if (fetchError) {
      log.error('Error fetching user data:', fetchError);
      throw fetchError;
    }
    
    // Then update the user to be inactive and change email to avoid conflicts
    const { data, error } = await supabase
      .from('users')
      .update({ 
        is_active: false,
        email: `deactivated_${Date.now()}_${userData.email}` // Make email unique to prevent conflicts
      })
      .eq('id', userId);
      
    if (error) {
      log.error('Error deactivating user:', error);
      throw error;
    }
    
    return { success: true, data };
  },
  // Venues
  async getVenues(filters = {}) {
    let query = supabase
      .from('venues')
      .select(`
        *,
        venue_categories (
          name,
          icon
        ),
        venue_cuisines (
          category,
          subcategory
        )
      `)
      .eq('is_active', true)

    if (filters.category) {
      query = query.eq('category_id', filters.category)
    }
    if (filters.city) {
      query = query.ilike('city', `%${filters.city}%`)
    }
    if (filters.minCapacity) {
      query = query.gte('capacity', filters.minCapacity)
    }

    // Filter by amenities if any are selected
    if (filters.amenities) {
      const selectedAmenities = Object.entries(filters.amenities)
        .filter(([_, value]) => value === true)
        .map(([key, _]) => key);

      // Only apply amenities filter if at least one amenity is selected
      if (selectedAmenities.length > 0) {
        // For each selected amenity, add a filter condition
        selectedAmenities.forEach(amenity => {
          // Use containedBy to check if the amenity exists in the JSONB array
          // or if the amenity key exists in the JSONB object with value true
          query = query.or(`amenities->>${amenity}.eq.true`);
        });
      }
    }

    return query.order('name')
  },

  async getVenueById(id) {
    return supabase
      .from('venues')
      .select(`
        *,
        venue_categories (
          name,
          icon
        )
      `)
      .eq('id', id)
      .single()
  },
  
  async createVenue(venueData) {
    log.info('=== SUPABASE CREATE VENUE DEBUG ===');
    log.info('Original venue data:', venueData);
    
    // Clean up the data before sending to database
    const cleanData = { ...venueData };
    
    // Convert empty strings to null for numeric fields
    if (cleanData.latitude === '' || cleanData.latitude === undefined) {
      cleanData.latitude = null;
    } else if (cleanData.latitude !== null) {
      cleanData.latitude = parseFloat(cleanData.latitude);
    }
    
    if (cleanData.longitude === '' || cleanData.longitude === undefined) {
      cleanData.longitude = null;
    } else if (cleanData.longitude !== null) {
      cleanData.longitude = parseFloat(cleanData.longitude);
    }
    
    // Handle other numeric fields
    if (cleanData.price_per_person === '' || cleanData.price_per_person === undefined) {
      cleanData.price_per_person = null;
    } else if (cleanData.price_per_person !== null) {
      cleanData.price_per_person = parseFloat(cleanData.price_per_person);
    }
    
    if (cleanData.deposit_required === '' || cleanData.deposit_required === undefined) {
      cleanData.deposit_required = null;
    } else if (cleanData.deposit_required !== null) {
      cleanData.deposit_required = parseFloat(cleanData.deposit_required);
    }
    
    if (cleanData.max_group_size === '' || cleanData.max_group_size === undefined) {
      cleanData.max_group_size = null;
    } else if (cleanData.max_group_size !== null) {
      cleanData.max_group_size = parseInt(cleanData.max_group_size);
    }
    
    // Convert empty strings to null for UUID fields
    if (cleanData.category_id === '' || cleanData.category_id === undefined) {
      cleanData.category_id = null;
    }
    
    if (cleanData.cuisine_id === '' || cleanData.cuisine_id === undefined) {
      cleanData.cuisine_id = null;
    }
    
    // Handle price level field (CPG-53 updated)
    if (cleanData.price_level === '' || cleanData.price_level === undefined) {
      cleanData.price_level = null;
    }
    
    if (cleanData.overall_rating === '' || cleanData.overall_rating === undefined) {
      cleanData.overall_rating = null;
    } else if (cleanData.overall_rating !== null) {
      cleanData.overall_rating = parseFloat(cleanData.overall_rating);
    }
    
    if (cleanData.user_ratings_total === '' || cleanData.user_ratings_total === undefined) {
      cleanData.user_ratings_total = null;
    } else if (cleanData.user_ratings_total !== null) {
      cleanData.user_ratings_total = parseInt(cleanData.user_ratings_total);
    }
    
    log.info('Clean venue data for insert:', cleanData);
    
    // DIAGNOSTIC: Try multiple approaches to isolate the issue
    
    // Test 1: Check if we can connect to the database at all
    log.info('TEST 1: Testing basic database connection...');
    try {
      // Use a simple timeout for the connection test too
      const connTimeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Connection test timed out after 3 seconds')), 3000);
      });
      
      const connectionTestPromise = supabase.from('venues').select('id').limit(1);
      const connectionTest = await Promise.race([connectionTestPromise, connTimeoutPromise]);
      log.info('Basic connection test result:', connectionTest);
    } catch (connErr) {
      log.error('Connection test failed:', connErr);
      
      // If connection test fails, try an even simpler approach
      log.info('FALLBACK: Trying direct insert without any checks...');
      try {
        const directInsertPromise = supabase
          .from('venues')
          .insert([cleanData]);
          
        const directTimeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Direct insert timed out after 5 seconds')), 5000);
        });
        
        const directResult = await Promise.race([directInsertPromise, directTimeoutPromise]);
        log.info('Direct insert result:', directResult);
        
        if (directResult && !directResult.error) {
          log.info('Direct insert succeeded! Returning success without select.');
          return { data: cleanData, error: null };
        }
      } catch (directErr) {
        log.error('Direct insert also failed:', directErr);
      }
    }
    
    // Test 2: Try insert without select to see if that's the issue
    log.info('TEST 2: Trying insert without select...');
    const simpleTimeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Simple insert timed out after 5 seconds')), 5000);
    });
    
    const simpleInsertPromise = supabase
      .from('venues')
      .insert([cleanData]);
    
    try {
      const simpleResult = await Promise.race([simpleInsertPromise, simpleTimeoutPromise]);
      log.info('Simple insert result:', simpleResult);
      
      // If simple insert worked, try to get the created record
      if (simpleResult && !simpleResult.error) {
        log.info('Simple insert succeeded, now trying to fetch the record...');
        
        // Get the most recently created venue by this user
        const fetchPromise = supabase
          .from('venues')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
          
        const fetchTimeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Fetch after insert timed out')), 5000);
        });
        
        const fetchedRecord = await Promise.race([fetchPromise, fetchTimeoutPromise]);
        log.info('Fetched record after simple insert:', fetchedRecord);
        
        return fetchedRecord;
      }
    } catch (simpleErr) {
      log.error('Simple insert failed:', simpleErr);
    }
    
    // Test 3: Original approach with timeout (fallback)
    log.info('TEST 3: Falling back to original approach...');
    
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Venue insert operation timed out after 10 seconds')), 10000);
    });
    
    const insertPromise = supabase
      .from('venues')
      .insert([cleanData])
      .select()
      .single();
    
    log.info('Starting venue insert with select...');
    
    const result = await Promise.race([insertPromise, timeoutPromise]);
    
    log.info('Create venue result:', result);
    log.info('=== SUPABASE CREATE VENUE END ===');
    
    return result;
  },

  async updateVenue(id, updates) {
    log.info('Updating venue:', id);
    
    // Clean up the data before sending to database
    const cleanUpdates = { ...updates };
    
    // Convert empty strings to null for numeric fields
    if (cleanUpdates.latitude === '' || cleanUpdates.latitude === undefined) {
      cleanUpdates.latitude = null;
    } else if (cleanUpdates.latitude !== null) {
      cleanUpdates.latitude = parseFloat(cleanUpdates.latitude);
    }
    
    if (cleanUpdates.longitude === '' || cleanUpdates.longitude === undefined) {
      cleanUpdates.longitude = null;
    } else if (cleanUpdates.longitude !== null) {
      cleanUpdates.longitude = parseFloat(cleanUpdates.longitude);
    }
    
    // Handle other numeric fields
    if (cleanUpdates.price_per_person === '' || cleanUpdates.price_per_person === undefined) {
      cleanUpdates.price_per_person = null;
    } else if (cleanUpdates.price_per_person !== null) {
      cleanUpdates.price_per_person = parseFloat(cleanUpdates.price_per_person);
    }
    
    if (cleanUpdates.deposit_required === '' || cleanUpdates.deposit_required === undefined) {
      cleanUpdates.deposit_required = null;
    } else if (cleanUpdates.deposit_required !== null) {
      cleanUpdates.deposit_required = parseFloat(cleanUpdates.deposit_required);
    }
    
    if (cleanUpdates.max_group_size === '' || cleanUpdates.max_group_size === undefined) {
      cleanUpdates.max_group_size = null;
    } else if (cleanUpdates.max_group_size !== null) {
      cleanUpdates.max_group_size = parseInt(cleanUpdates.max_group_size);
    }
    
    if (cleanUpdates.min_days_advance === '' || cleanUpdates.min_days_advance === undefined) {
      cleanUpdates.min_days_advance = null;
    } else if (cleanUpdates.min_days_advance !== null) {
      cleanUpdates.min_days_advance = parseInt(cleanUpdates.min_days_advance);
    }
    
    // Convert empty strings to null for UUID fields
    if (cleanUpdates.category_id === '' || cleanUpdates.category_id === undefined) {
      cleanUpdates.category_id = null;
    }
    
    if (cleanUpdates.cuisine_id === '' || cleanUpdates.cuisine_id === undefined) {
      cleanUpdates.cuisine_id = null;
    }
    
    // Handle price level field (CPG-53 updated)
    if (cleanUpdates.price_level === '' || cleanUpdates.price_level === undefined) {
      cleanUpdates.price_level = null;
    }
    
    if (cleanUpdates.overall_rating === '' || cleanUpdates.overall_rating === undefined) {
      cleanUpdates.overall_rating = null;
    } else if (cleanUpdates.overall_rating !== null) {
      cleanUpdates.overall_rating = parseFloat(cleanUpdates.overall_rating);
    }
    
    if (cleanUpdates.user_ratings_total === '' || cleanUpdates.user_ratings_total === undefined) {
      cleanUpdates.user_ratings_total = null;
    } else if (cleanUpdates.user_ratings_total !== null) {
      cleanUpdates.user_ratings_total = parseInt(cleanUpdates.user_ratings_total);
    }
    
    try {
      // Get the current session token for authentication
      const session = await supabase.auth.getSession();
      const token = session?.data?.session?.access_token;
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      // Use direct fetch instead of supabase client (resolves timeout issues)
      const url = `${supabase.supabaseUrl}/rest/v1/venues?id=eq.${id}`;
      const headers = {
        'apikey': supabase.supabaseKey,
        'Authorization': `Bearer ${token}`, // Use user's JWT token instead of anon key
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      };
      
      log.info('Direct fetch URL:', url);
      
      const fetchPromise = fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(cleanUpdates)
      });
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Direct fetch update timed out after 5 seconds')), 5000);
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (!response.ok) {
        const errorText = await response.text();
        log.error('Venue update failed:', response.status, response.statusText, errorText);
        throw new Error(`Update failed: ${response.status} ${response.statusText}`);
      }
      
      const responseText = await response.text();
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        log.warn('Could not parse update response as JSON');
        data = null;
      }
      
      log.info('✅ Venue updated successfully');
      
      // Return in the same format as supabase client
      return {
        data: Array.isArray(data) ? data[0] : data,
        error: null,
        status: response.status,
        statusText: response.statusText
      };
      
    } catch (directError) {
      log.error('Direct fetch update failed, trying supabase client:', directError.message);
      
      // Fallback to original supabase client with shorter timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Venue update timed out')), 3000);
      });
      
      const updatePromise = supabase
        .from('venues')
        .update(cleanUpdates)
        .eq('id', id)
        .select()
        .single();
      
      try {
        const result = await Promise.race([updatePromise, timeoutPromise]);
        log.info('✅ Venue updated via supabase client');
        return result;
      } catch (fallbackError) {
        log.error('Both update methods failed:', fallbackError.message);
        return { error: fallbackError };
      }
    }
  },
  
  // Special function for venue managers to update their venue
  // This uses a stored procedure approach to bypass RLS
  async updateVenueForManager(userId, venueId, updates) {
    // First verify that the user is a venue manager and has permission to update this venue
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role, venue_id')
      .eq('id', userId)
      .single();
      
    if (userError) {
      log.error('Error fetching user data:', userError);
      return { error: userError };
    }
    
    // Check if user is a venue manager and has permission to update this venue
    if (userData.role !== 'venue_manager' || userData.venue_id !== venueId) {
      return { 
        error: { 
          message: 'You do not have permission to update this venue' 
        } 
      };
    }
    
    // Use RPC (Remote Procedure Call) to update the venue
    // This bypasses RLS because it's a direct SQL query
    const { error } = await supabase.rpc('update_venue_for_manager', {
      p_venue_id: venueId,
      p_updates: updates
    });
    
    if (error) {
      log.error('Error updating venue:', error);
      return { error };
    }
    
    // Fetch the updated venue
    const { data: venueData, error: venueError } = await supabase
      .from('venues')
      .select('*')
      .eq('id', venueId)
      .single();
      
    if (venueError) {
      log.error('Error fetching updated venue:', venueError);
      return { error: venueError };
    }
    
    return { data: venueData };
  },
  
  async deleteVenue(id) {
    // Soft delete by setting is_active to false
    return supabase
      .from('venues')
      .update({ is_active: false })
      .eq('id', id)
      .select()
      .single()
  },

  // Teams
  async getUserTeams(userId, includeAllManagers = true) {
    if (!includeAllManagers) {
      // Lightweight version for reservation creation - only get basic team info
      return await supabase
        .from('teams')
        .select(`
          id,
          name,
          company,
          default_group_size,
          is_active,
          sport_activity,
          state,
          division,
          user_team_roles!inner (
            user_id,
            role
          )
        `)
        .eq('user_team_roles.user_id', userId)
        .eq('is_active', true)
        .order('name');
    }

    // Full version with all managers (existing functionality)
    const { data: userTeams, error: teamsError } = await supabase
      .from('teams')
      .select(`
        *,
        user_team_roles!inner (
          user_id,
          role
        )
      `)
      .eq('user_team_roles.user_id', userId)
      .eq('is_active', true)
      .order('name');
    
    if (teamsError || !userTeams) {
      return { data: userTeams, error: teamsError };
    }
    
    // Now fetch all team managers for these teams
    const teamIds = userTeams.map(team => team.id);
    if (teamIds.length === 0) {
      return { data: userTeams, error: null };
    }
    
    const { data: allTeamManagers, error: managersError } = await supabase
      .from('user_team_roles')
      .select(`
        team_id,
        user_id,
        role,
        users (
          id,
          first_name,
          last_name,
          email
        )
      `)
      .in('team_id', teamIds);
    
    if (managersError) {
      return { data: userTeams, error: managersError };
    }
    
    // Merge the team managers data into the teams
    const teamsWithManagers = userTeams.map(team => {
      const managers = allTeamManagers?.filter(tm => tm.team_id === team.id) || [];
      return {
        ...team,
        user_team_roles: managers.map(manager => ({
          user_id: manager.user_id,
          role: manager.role,
          users: manager.users
        }))
      };
    });
    
    return { data: teamsWithManagers, error: null };
  },

  // Team Managers - updated for new user_team_roles table
  async getTeamManagers(teamId) {
    return supabase
      .from('user_team_roles')
      .select(`
        *,
        users (
          id,
          email,
          first_name,
          last_name,
          role
        )
      `)
      .eq('team_id', teamId)
      .order('role', { ascending: false }) // primary_manager comes before assistant_manager
      .order('users.first_name')
  },

  async addTeamManager(teamId, userId, role = 'primary_manager') {
    return supabase
      .from('user_team_roles')
      .upsert({
        team_id: teamId,
        user_id: userId,
        role: role
      }, {
        onConflict: 'user_id,team_id'
      })
      .select()
      .single()
  },

  async removeTeamManager(teamId, userId) {
    return supabase
      .from('user_team_roles')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', userId)
  },

  async updateTeamManagerRole(teamId, userId, newRole) {
    // Convert legacy parameters to new role format
    const roleValue = newRole === 'primary' ? 'primary_manager' : 'assistant_manager';
    
    return supabase
      .from('user_team_roles')
      .update({ 
        role: roleValue
      })
      .eq('team_id', teamId)
      .eq('user_id', userId)
      .select()
      .single()
  },

  async getPrimaryManager(teamId) {
    return supabase
      .from('user_team_roles')
      .select(`
        *,
        users (
          id,
          email,
          first_name,
          last_name,
          role
        )
      `)
      .eq('team_id', teamId)
      .eq('role', 'primary_manager')
      .single()
  },

  async isTeamManager(teamId, userId) {
    const { data, error } = await supabase
      .from('user_team_roles')
      .select('id')
      .eq('team_id', teamId)
      .eq('user_id', userId)
      .in('role', ['primary_manager', 'assistant_manager'])
      .single()
    
    return { isManager: !!data && !error, error }
  },

  async createTeam(teamData) {
    return supabase
      .from('teams')
      .insert([teamData])
      .select()
      .single()
  },

  async updateTeam(id, updates) {
    return supabase
      .from('teams')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
  },

  // Team Members
  async getTeamMembers(teamId) {
    return supabase
      .from('team_members')
      .select('*')
      .eq('team_id', teamId)
      .eq('is_active', true)
      .order('last_name', { ascending: true })
  },

  async addTeamMember(memberData) {
    return supabase
      .from('team_members')
      .insert([memberData])
      .select()
      .single()
  },

  async updateTeamMember(id, updates) {
    return supabase
      .from('team_members')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
  },

  // Reservations
  async getTeamReservations(teamId) {
    return supabase
      .from('reservations')
      .select(`
        *,
        venues (
          name,
          address_line1,
          address_line2,
          city,
          state,
          zip_code,
          phone,
          website,
          contact_person,
          venue_categories (
            name
          ),
          venue_cuisines (
            category,
            subcategory
          )
        ),
        teams (
          name
        )
      `)
      .eq('team_id', teamId)
      .order('reservation_date', { ascending: true })
  },

  async createReservation(reservationData) {
    return supabase
      .from('reservations')
      .insert([reservationData])
      .select(`
        *,
        venues (
          name,
          address_line1,
          address_line2,
          city,
          state,
          zip_code,
          phone,
          website,
          contact_person,
          venue_categories (
            name
          ),
          venue_cuisines (
            category,
            subcategory
          )
        )
      `)
      .single()
  },

  async updateReservation(id, updates) {
    return supabase
      .from('reservations')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
  },
  
  // Special function for venue managers to update reservation status
  async updateReservationStatus(userId, reservationId, newStatus) {
    // First verify that the user is a venue manager and has permission to update this reservation
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role, venue_id')
      .eq('id', userId)
      .single();
      
    if (userError) {
      log.error('Error fetching user data:', userError);
      return { error: userError };
    }
    
    // Check if user is a venue manager
    if (userData.role !== 'venue_manager' || !userData.venue_id) {
      return { 
        error: { 
          message: 'You do not have permission to update reservation status' 
        } 
      };
    }
    
    // Get the reservation to check if it belongs to the venue manager's venue
    const { data: reservationData, error: reservationError } = await supabase
      .from('reservations')
      .select('venue_id')
      .eq('id', reservationId)
      .single();
      
    if (reservationError) {
      log.error('Error fetching reservation data:', reservationError);
      return { error: reservationError };
    }
    
    // Check if the reservation belongs to the venue manager's venue
    if (reservationData.venue_id !== userData.venue_id) {
      return { 
        error: { 
          message: 'You do not have permission to update this reservation' 
        } 
      };
    }
    
    // Validate the new status
    const validStatuses = ['pending', 'confirmed', 'cancelled', 'completed', 'no_show'];
    if (!validStatuses.includes(newStatus)) {
      return {
        error: {
          message: 'Invalid status. Status must be one of: ' + validStatuses.join(', ')
        }
      };
    }
    
    // Update the reservation status
    const { data, error } = await supabase
      .from('reservations')
      .update({ status: newStatus })
      .eq('id', reservationId)
      .select()
      .single();
      
    if (error) {
      log.error('Error updating reservation status:', error);
      return { error };
    }
    
    return { data };
  },
  
  // Function for team managers to update their own reservations
  async updateTeamManagerReservation(userId, reservationId, updates) {
    // First verify that the user is the creator of this reservation
    const { data: reservationData, error: reservationError } = await supabase
      .from('reservations')
      .select('created_by, status, reserved_at')
      .eq('id', reservationId)
      .single();
      
    if (reservationError) {
      log.error('Error fetching reservation data:', reservationError);
      return { error: reservationError };
    }
    
    // Check if user is the creator of the reservation
    if (reservationData.created_by !== userId) {
      return { 
        error: { 
          message: 'You do not have permission to update this reservation' 
        } 
      };
    }
    
    // Check if this is a status update or a full reservation update
    if (updates.status && updates.status !== reservationData.status) {
      // Only allow cancellation if the current status is pending or confirmed
      if (updates.status === 'cancelled') {
        if (!['pending', 'confirmed'].includes(reservationData.status)) {
          return {
            error: {
              message: `Cannot cancel a reservation with status: ${reservationData.status}`
            }
          };
        }
      } else {
        // Team managers can only set status to cancelled
        return {
          error: {
            message: 'Team managers can only cancel reservations'
          }
        };
      }
    } else if (Object.keys(updates).length > 1 || !updates.status) {
      // This is a full reservation update, check if it's more than 24 hours in the future
      const reservationDateTime = new Date(reservationData.reserved_at);
      const now = new Date();
      const hoursDifference = (reservationDateTime - now) / (1000 * 60 * 60);
      
      if (hoursDifference < 24) {
        return {
          error: {
            message: 'Reservations can only be updated if they are more than 24 hours in the future'
          }
        };
      }
      
      // Only allow updating if status is pending or confirmed
      if (!['pending', 'confirmed'].includes(reservationData.status)) {
        return {
          error: {
            message: `Cannot update a reservation with status: ${reservationData.status}`
          }
        };
      }
    }
    
    // Update the reservation
    const { data, error } = await supabase
      .from('reservations')
      .update(updates)
      .eq('id', reservationId)
      .select()
      .single();
      
    if (error) {
      log.error('Error updating reservation:', error);
      return { error };
    }
    
    return { data };
  },
  
  // Function for TeamRezy employees to update any reservation
  async updateAnyReservation(userId, reservationId, updates) {
    // First verify that the user is a TeamRezy employee
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .single();
      
    if (userError) {
      log.error('Error fetching user data:', userError);
      return { error: userError };
    }
    
    // Check if user is a TeamRezy employee
    if (userData.role !== 'teamrezy_employee') {
      return { 
        error: { 
          message: 'Only TeamRezy employees can update any reservation' 
        } 
      };
    }
    
    // Validate the status if it's being updated
    if (updates.status) {
      const validStatuses = ['pending', 'confirmed', 'cancelled', 'completed', 'no_show'];
      if (!validStatuses.includes(updates.status)) {
        return {
          error: {
            message: 'Invalid status. Status must be one of: ' + validStatuses.join(', ')
          }
        };
      }
    }
    
    // Update the reservation
    const { data, error } = await supabase
      .from('reservations')
      .update(updates)
      .eq('id', reservationId)
      .select(`
        *,
        venues (
          name,
          address_line1,
          address_line2,
          city,
          state,
          zip_code,
          phone,
          website,
          contact_person,
          venue_categories (
            name
          ),
          venue_cuisines (
            category,
            subcategory
          )
        ),
        teams (
          name
        )
      `)
      .single();
      
    if (error) {
      log.error('Error updating reservation:', error);
      return { error };
    }
    
    return { data };
  },

  async cancelReservation(id) {
    return supabase
      .from('reservations')
      .update({ status: 'cancelled' })
      .eq('id', id)
      .select()
      .single()
  },

  // Venue Categories
  async getVenueCategories() {
    return supabase
      .from('venue_categories')
      .select('*')
      .order('name')
  },

  // Venue Availability
  async checkVenueAvailability(venueId, date) {
    return supabase
      .from('venue_availability')
      .select('*')
      .eq('venue_id', venueId)
      .eq('date', date)
      .eq('is_available', false)
  },

  // Reviews
  async getVenueReviews(venueId) {
    return supabase
      .from('reviews')
      .select(`
        *,
        users (
          first_name,
          last_name,
          company
        )
      `)
      .eq('venue_id', venueId)
      .order('created_at', { ascending: false })
  },

  async createReview(reviewData) {
    return supabase
      .from('reviews')
      .insert([reviewData])
      .select()
      .single()
  },

  // Pending Users Management
  async getPendingUsers() {
    return supabase
      .from('pending_users')
      .select(`
        *,
        teams:selected_team_id (
          name,
          company
        )
      `)
      .eq('status', 'pending')
      .order('requested_at', { ascending: false })
  },

  async approvePendingUser(pendingUserId, approvedBy) {
    return supabase.rpc('approve_pending_user', {
      p_pending_user_id: pendingUserId,
      p_approved_by: approvedBy
    })
  },

  async rejectPendingUser(pendingUserId, rejectedBy, rejectionReason) {
    return supabase.rpc('reject_pending_user', {
      p_pending_user_id: pendingUserId,
      p_rejected_by: rejectedBy,
      p_rejection_reason: rejectionReason
    })
  },

  // Geocoding Jobs Management
  async getGeocodingJobsStats() {
    try {
      // First try using the database function which bypasses RLS
      const { data: functionResult, error: functionError } = await supabase
        .rpc('get_geocoding_stats');
      
      if (!functionError && functionResult && !functionResult.error) {
        log.info('✅ Geocoding stats from function:', functionResult);
        return { data: functionResult, error: null };
      }
      
      log.info('⚠️ Function approach failed, trying direct query:', functionError);
      
      // Fallback to direct query
      const { data: jobs, error: queryError } = await supabase
        .from('geocoding_jobs')
        .select('status');
      
      if (queryError) {
        log.error('❌ Direct query also failed:', queryError);
        return { data: null, error: queryError };
      }
      
      // Process the results
      const stats = {
        pending: 0,
        processing: 0,
        completed: 0,
        failed: 0,
        total: jobs?.length || 0
      };
      
      jobs?.forEach(job => {
        if (Object.prototype.hasOwnProperty.call(stats, job.status)) {
          stats[job.status]++;
        }
      });
      
      log.info('✅ Geocoding stats from direct query:', stats);
      return { data: stats, error: null };
      
    } catch (error) {
      log.error('❌ Error getting geocoding stats:', error);
      return { data: null, error };
    }
  },

  async processGeocodingJobs(options = {}) {
    const { limit = 50, max_attempts = 3 } = options;
    
    try {
      // Use Supabase client to call the Edge function with proper authentication
      const { data, error } = await supabase.functions.invoke('process-geocoding-jobs', {
        body: { limit, max_attempts }
      });
      
      if (error) {
        log.error('❌ Edge function error:', error);
        throw new Error(error.message || 'Failed to process geocoding jobs');
      }
      
      log.info('✅ Edge function response:', data);
      
      // Ensure we return a consistent data structure
      if (!data) {
        return {
          processed: 0,
          succeeded: 0,
          failed: 0,
          message: 'No data returned from geocoding function'
        };
      }
      
      // Return the data with default values if properties are missing
      return {
        processed: data.processed || 0,
        succeeded: data.succeeded || 0,
        failed: data.failed || 0,
        message: data.message || 'Geocoding jobs processed',
        ...data // Include any other properties from the response
      };
      
    } catch (error) {
      log.error('❌ Error processing geocoding jobs:', error);
      throw error;
    }
  },

  // Reset failed geocoding jobs to pending status for retry
  async resetFailedGeocodingJobs() {
    try {
      const { data, error } = await supabase
        .from('geocoding_jobs')
        .update({ 
          status: 'pending',
          error_message: null,
          updated_at: new Date().toISOString()
        })
        .eq('status', 'failed')
        .select();

      if (error) {
        log.error('❌ Error resetting failed geocoding jobs:', error);
        throw new Error(error.message || 'Failed to reset failed geocoding jobs');
      }

      log.info('✅ Reset failed geocoding jobs:', data?.length || 0);
      return { data: { reset_count: data?.length || 0 }, error: null };
    } catch (error) {
      log.error('❌ Error resetting failed geocoding jobs:', error);
      return { data: null, error };
    }
  },

  // Distance and Travel Time Calculations
  async calculateDistanceAndTime(origin, destination) {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      log.warn('Google Maps API key not configured, using straight-line distance calculation');
      // Fall back to straight-line distance calculation
      return this._calculateStraightLineDistance(origin, destination);
    }

    // Validate inputs
    if (!origin || !destination) {
      throw new Error('Origin and destination are required');
    }

    const originStr = typeof origin === 'object' 
      ? `${origin.latitude},${origin.longitude}`
      : origin;
    const destinationStr = typeof destination === 'object' 
      ? `${destination.latitude},${destination.longitude}`
      : destination;

    try {
      // First try with a basic fetch call
      const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${encodeURIComponent(originStr)}&destinations=${encodeURIComponent(destinationStr)}&key=${apiKey}&units=imperial`;
      
      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'OK' && data.rows?.[0]?.elements?.[0]?.status === 'OK') {
        const element = data.rows[0].elements[0];
        return {
          distance: element.distance?.text || 'N/A',
          duration: element.duration?.text || 'N/A',
          distanceValue: element.distance?.value || 0, // in meters
          durationValue: element.duration?.value || 0  // in seconds
        };
      } else {
        log.info('Google Maps API failed, using straight-line distance calculation');
        return this._calculateStraightLineDistance(origin, destination);
      }
    } catch (error) {
      log.info('Distance calculation failed, using straight-line distance:', error);
      return this._calculateStraightLineDistance(origin, destination);
    }
  },

  // Helper method for straight-line distance calculation
  _calculateStraightLineDistance(origin, destination) {
    const origin_coords = typeof origin === 'object' ? origin : { latitude: 0, longitude: 0 };
    const dest_coords = typeof destination === 'object' ? destination : { latitude: 0, longitude: 0 };
    
    // Validate coordinates
    if (!origin_coords.latitude || !origin_coords.longitude || 
        !dest_coords.latitude || !dest_coords.longitude) {
      return {
        distance: 'N/A',
        duration: 'N/A',
        distanceValue: 0,
        durationValue: 0
      };
    }
    
    const R = 3959; // Earth's radius in miles
    const dLat = (dest_coords.latitude - origin_coords.latitude) * Math.PI / 180;
    const dLon = (dest_coords.longitude - origin_coords.longitude) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(origin_coords.latitude * Math.PI / 180) * Math.cos(dest_coords.latitude * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    const estimatedDriveTime = Math.round(distance * 2); // Rough estimate: 2 minutes per mile
    
    return {
      distance: `${distance.toFixed(1)} mi`,
      duration: `~${estimatedDriveTime} mins`,
      distanceValue: distance * 1609.34, // Convert to meters
      durationValue: estimatedDriveTime * 60 // Convert to seconds
    };
  },

  async calculateDistancesToVenues(gameLocationId, venues) {
    try {
      // Get the game location coordinates
      const { data: gameLocation, error: gameLocationError } = await supabase
        .from('game_locations')
        .select('id, name, latitude, longitude, address_line1, city, state')
        .eq('id', gameLocationId)
        .single();

      if (gameLocationError || !gameLocation || !gameLocation.latitude || !gameLocation.longitude) {
        log.error('Game location not found or missing coordinates:', gameLocationError);
        return venues.map(venue => ({ ...venue, distanceInfo: null }));
      }

      // Calculate distances for all venues
      const venuesWithDistance = await Promise.all(
        venues.map(async (venue) => {
          if (!venue.latitude || !venue.longitude) {
            return { ...venue, distanceInfo: null };
          }

          const distanceInfo = await this.calculateDistanceAndTime(
            { latitude: gameLocation.latitude, longitude: gameLocation.longitude },
            { latitude: venue.latitude, longitude: venue.longitude }
          );

          return {
            ...venue,
            distanceInfo: distanceInfo ? {
              ...distanceInfo,
              gameLocationName: gameLocation.name
            } : null
          };
        })
      );

      // Sort venues by distance (closest first)
      return venuesWithDistance.sort((a, b) => {
        if (!a.distanceInfo) return 1;
        if (!b.distanceInfo) return -1;
        return a.distanceInfo.distanceValue - b.distanceInfo.distanceValue;
      });

    } catch (error) {
      log.error('Error calculating distances to venues:', error);
      return venues.map(venue => ({ ...venue, distanceInfo: null }));
    }
  },

  // Team Tournament Hotels Management
  async getTeamTournamentHotels(teamId, tournamentId = null) {
    let query = supabase
      .from('team_tournament_hotels')
      .select(`
        *,
        teams (name),
        tournaments (name, start_date, end_date, host_city, host_state, timezone)
      `)
      .eq('team_id', teamId);

    if (tournamentId) {
      query = query.eq('tournament_id', tournamentId);
    }

    return query.order('created_at', { ascending: false });
  },

  async createTeamTournamentHotel(hotelData) {
    // Use upsert to handle potential duplicates
    // First, try to find existing hotel for this team+tournament
    const { data: existing } = await supabase
      .from('team_tournament_hotels')
      .select('id')
      .eq('team_id', hotelData.team_id)
      .eq('tournament_id', hotelData.tournament_id)
      .single();

    if (existing) {
      // Update existing record
      return supabase
        .from('team_tournament_hotels')
        .update(hotelData)
        .eq('id', existing.id)
        .select(`
          *,
          teams (name),
          tournaments (name, start_date, end_date, host_city, host_state, timezone)
        `)
        .single();
    } else {
      // Insert new record
      return supabase
        .from('team_tournament_hotels')
        .insert([hotelData])
        .select(`
          *,
          teams (name),
          tournaments (name, start_date, end_date, host_city, host_state, timezone)
        `)
        .single();
    }
  },

  async updateTeamTournamentHotel(id, updates) {
    return supabase
      .from('team_tournament_hotels')
      .update(updates)
      .eq('id', id)
      .select(`
        *,
        teams (name),
        tournaments (name, start_date, end_date, host_city, host_state, timezone)
      `)
      .single();
  },

  async deleteTeamTournamentHotel(id) {
    return supabase
      .from('team_tournament_hotels')
      .delete()
      .eq('id', id);
  },

  // Tournament search functions
  async searchAvailableTournaments(searchTerm = '', userTeamIds = []) {
    log.info('🔍 Searching tournaments with term:', searchTerm, 'excluding teams:', userTeamIds);
    
    const today = new Date().toISOString().split('T')[0];
    let query = supabase
      .from('tournaments')
      .select(`
        *,
        tournament_types (
          id,
          name,
          description
        )
      `)
      .eq('is_active', true)
      .gte('end_date', today)
      .order('start_date', { ascending: true });

    if (searchTerm) {
      query = query.or(`name.ilike.%${searchTerm}%,host_city.ilike.%${searchTerm}%,host_state.ilike.%${searchTerm}%`);
    }

    const { data: allTournaments, error } = await query;
    
    if (error) {
      log.error('❌ Error fetching tournaments:', error);
      return { data: null, error };
    }

    // If no user teams provided, return all tournaments with divisions
    if (!userTeamIds || userTeamIds.length === 0) {
      log.info('✅ No team filtering needed, adding divisions to all tournaments:', allTournaments?.length || 0);
      
      // Add divisions to each tournament
      const tournamentsWithDivisions = [];
      for (const tournament of allTournaments || []) {
        const { data: tournamentDivisions } = await supabase
          .from('tournament_tournament_divisions')
          .select(`
            division_id,
            tournament_divisions (
              id,
              name,
              description,
              skill_level
            )
          `)
          .eq('tournament_id', tournament.id);
        
        tournamentsWithDivisions.push({
          ...tournament,
          tournament_divisions: tournamentDivisions || []
        });
      }
      
      return { data: tournamentsWithDivisions, error: null };
    }

    // Filter out tournaments where any of the user's teams are already registered
    const availableTournaments = [];
    
    for (const tournament of allTournaments || []) {
      // Check if any of the user's teams are already in this tournament
      const { data: existingRegistrations } = await supabase
        .from('tournament_teams')
        .select('team_id')
        .eq('tournament_id', tournament.id)
        .in('team_id', userTeamIds);
      
      // If no teams are registered for this tournament, it's available
      if (!existingRegistrations || existingRegistrations.length === 0) {
        // Get tournament divisions for this tournament
        const { data: tournamentDivisions } = await supabase
          .from('tournament_tournament_divisions')
          .select(`
            division_id,
            tournament_divisions (
              id,
              name,
              description,
              skill_level
            )
          `)
          .eq('tournament_id', tournament.id);
        
        availableTournaments.push({
          ...tournament,
          tournament_divisions: tournamentDivisions || []
        });
      } else {
        log.info('🚫 Excluding tournament', tournament.name, '- teams already registered:', existingRegistrations.map(r => r.team_id));
      }
    }

    log.info('✅ Available tournaments after filtering:', availableTournaments.length);
    return { data: availableTournaments, error: null };
  },

  async getTeamsInTournament(tournamentId) {
    // Use the database function to bypass RLS and get all teams in tournament
    log.info('🔍 Getting teams in tournament:', tournamentId);
    
    const { data, error } = await supabase
      .rpc('get_tournament_teams', { tournament_id_param: tournamentId });
    
    if (error) {
      log.error('❌ Error getting tournament teams:', error);
      return { data: null, error };
    }
    
    log.info('✅ Found teams in tournament:', data?.length || 0);
    
    // Transform the data to match the expected format
    const transformedData = data?.map(team => ({
      team_id: team.team_id,
      teams: {
        id: team.team_id,
        name: team.team_name,
        sport_activity: team.sport_activity,
        company: team.company
      }
    })) || [];
    
    return { data: transformedData, error: null };
  },


  async getTeamTournamentsWithGames(teamId, options = {}) {
    const { filterGamesByTeam = true, managedTeamIds = null } = options;
    // Get tournaments where the team is registered (whether they have games or not)
    log.info('🔍 Getting tournaments for team ID:', teamId, 'with options:', { filterGamesByTeam, managedTeamIds });
    
    const today = new Date().toISOString().split('T')[0];
    log.info('🔍 Looking for tournaments starting after:', today);
    
    // First get all tournaments where this team is registered
    const { data: teamTournaments, error: teamTournamentsError } = await supabase
      .from('tournament_teams')
      .select(`
        tournament_id,
        tournaments!inner (
          id,
          name,
          description,
          start_date,
          end_date,
          host_city,
          host_state,
          sport,
          timezone,
          is_active,
          created_at,
          updated_at,
          gamecenter_url,
          type_id,
          tournament_types (
            id,
            name,
            description
          )
        )
      `)
      .eq('team_id', teamId);
    
    if (teamTournamentsError) {
      log.error('❌ Error fetching team tournaments:', teamTournamentsError);
      return { data: null, error: teamTournamentsError };
    }
    
    log.info('🔍 Team tournaments found:', teamTournaments?.length || 0);
    
    // Extract tournaments and filter by date
    // Show tournaments that are either upcoming or currently ongoing
    const tournaments = (teamTournaments || [])
      .map(tt => tt.tournaments)
      .filter(tournament => {
        // Show if tournament hasn't ended yet (end_date is today or in the future)
        return tournament.end_date >= today;
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    
    log.info('🔍 Active and upcoming team tournaments:', tournaments?.length || 0);
    if (tournaments.length > 0) {
      log.info('🔍 Tournament names:', tournaments.map(t => t.name));
    }
    
    // For each tournament, get games and hotel information
    const tournamentsWithData = [];
    
    for (const tournament of tournaments) {
      log.info(`🔍 Processing tournament "${tournament.name}" (ID: ${tournament.id})...`);
      
      // Get games for this tournament - filter by team and date
      let gamesQuery = supabase
        .from('games')
        .select(`
          id, 
          starts_at,
          ends_at,
          timezone,
          location_id,
          home_team_id, 
          away_team_id,
          status,
          score_home,
          score_away,
          notes,
          gamecenter_url,
          home_team:teams!home_team_id(id, name),
          away_team:teams!away_team_id(id, name),
          game_locations(id, name, city, state, latitude, longitude)
        `)
        .eq('tournament_id', tournament.id)
        .gte('starts_at', today); // Only show games that are today or in the future
      
      // Apply team filtering if requested
      if (filterGamesByTeam) {
        if (managedTeamIds && managedTeamIds.length > 0) {
          // For team managers: show games where any of their managed teams are playing
          const teamFilters = managedTeamIds.map(id => `home_team_id.eq.${id},away_team_id.eq.${id}`).join(',');
          gamesQuery = gamesQuery.or(teamFilters);
        } else {
          // For reservation booking: show games for the specific selected team
          gamesQuery = gamesQuery.or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`);
        }
      }
      
      const { data: games, error: gamesError } = await gamesQuery.order('starts_at', { ascending: true });
      
      log.info(`🔍 Games found for tournament "${tournament.name}":`, games?.length || 0);
      if (games?.length > 0) {
        log.info('🔍 Game details:', games.map(g => ({
          id: g.id,
          home_team: g.home_team?.name,
          away_team: g.away_team?.name,
          starts_at: g.starts_at
        })));
      }
      
      if (gamesError) {
        log.error(`❌ Error fetching games for tournament "${tournament.name}":`, gamesError);
      }
      
      // Get hotel information for this team and tournament
      const { data: hotelData, error: hotelError } = await supabase
        .from('team_tournament_hotels')
        .select(`
          id,
          hotel_name,
          hotel_address,
          latitude,
          longitude,
          place_id,
          phone_number,
          website_url,
          rating,
          price_level,
          created_at
        `)
        .eq('team_id', teamId)
        .eq('tournament_id', tournament.id)
        .maybeSingle();
      
      // Get main tournament location
      const { data: mainLocation, error: mainLocationError } = await supabase
        .from('tournament_locations')
        .select(`
          location_id,
          game_locations (
            id,
            name,
            address_line1,
            city,
            state,
            latitude,
            longitude,
            place_id
          )
        `)
        .eq('tournament_id', tournament.id)
        .eq('is_main_location', true)
        .maybeSingle();
      
      if (mainLocationError) {
        log.error(`❌ Error fetching main location for tournament "${tournament.name}":`, mainLocationError);
      }
      
      // Get tournament divisions
      const { data: tournamentDivisions, error: divisionsError } = await supabase
        .from('tournament_tournament_divisions')
        .select(`
          division_id,
          tournament_divisions (
            id,
            name,
            description,
            skill_level
          )
        `)
        .eq('tournament_id', tournament.id);
      
      if (divisionsError) {
        log.error(`❌ Error fetching divisions for tournament "${tournament.name}":`, divisionsError);
      }
      
      // Get all teams registered for this tournament for team filtering
      const { data: registeredTeams, error: registeredTeamsError } = await supabase
        .from('tournament_teams')
        .select(`
          team_id,
          teams!inner (
            id,
            name
          )
        `)
        .eq('tournament_id', tournament.id);
      
      if (registeredTeamsError) {
        log.error(`❌ Error fetching registered teams for tournament "${tournament.name}":`, registeredTeamsError);
      }
      
      // Include the tournament regardless of whether it has games or not
      tournamentsWithData.push({
        ...tournament,
        games: gamesError ? [] : (games || []),
        teamHotel: hotelError ? null : hotelData,
        mainLocation: mainLocationError ? null : mainLocation,
        registeredTeams: registeredTeamsError ? [] : (registeredTeams || []),
        tournament_divisions: divisionsError ? [] : (tournamentDivisions || [])
      });
    }
    
    log.info('🎯 Final result - tournaments for team:', tournamentsWithData.length);
    if (tournamentsWithData.length > 0) {
      log.info('🎯 Tournament names:', tournamentsWithData.map(t => t.name));
    }
    
    const result = { data: tournamentsWithData, error: null };
    return result;
  },

  // Alternative function for debugging - get all tournaments for a team (even without games)
  async getAllTournamentsForTeamDebug(teamId) {
    log.info('🐛 DEBUG: Getting ALL tournaments for team ID:', teamId);
    
    const today = new Date().toISOString().split('T')[0];
    log.info('🐛 DEBUG: Today date:', today);
    
    // Get all active and upcoming tournaments (that haven't ended yet)
    const { data: tournaments, error: tournamentsError } = await supabase
      .from('tournaments')
      .select('*')
      .gte('end_date', today)
      .order('start_date', { ascending: true });
    
    log.info('🐛 DEBUG: All tournaments found:', tournaments?.length || 0);
    
    if (tournamentsError) {
      log.error('🐛 DEBUG: Error fetching tournaments:', tournamentsError);
      return { data: null, error: tournamentsError };
    }

    // Also check what games exist for this team in ANY tournament
    const { data: allGames } = await supabase
      .from('games')
      .select(`
        id, 
        tournament_id,
        starts_at,
        home_team_id, 
        away_team_id,
        tournaments!tournament_id(name)
      `)
      .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`);

    log.info('🐛 DEBUG: All games for this team:', allGames?.length || 0);
    if (allGames?.length > 0) {
      log.info('🐛 DEBUG: Games by tournament:', allGames.map(g => ({
        tournament: g.tournaments?.name,
        tournament_id: g.tournament_id,
        starts_at: g.starts_at,
        home_team_id: g.home_team_id,
        away_team_id: g.away_team_id
      })));
    }

    return { 
      data: tournaments || [], 
      error: null,
      debug: {
        allGamesForTeam: allGames || []
      }
    };
  },

  // Google Places API integration
  async searchHotels(query, location = null) {
    // This will be called from the frontend component using Google Places API
    // We're including this here for completeness but the actual API calls
    // will be made directly from the React component
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      throw new Error('Google Maps API key not configured');
    }

    // Validate input
    if (!query || typeof query !== 'string') {
      throw new Error('Search query is required and must be a string');
    }

    // This is a placeholder - actual implementation will be in the React component
    // using Google Places Autocomplete and Place Details APIs
    return {
      apiKey,
      searchQuery: query.trim(),
      location
    };
  },

  // Calculate distances from hotel to venues
  async calculateDistanceFromHotel(hotelCoords, venues) {
    try {
      if (!hotelCoords || !hotelCoords.latitude || !hotelCoords.longitude) {
        return venues.map(venue => ({ ...venue, hotelDistanceInfo: null }));
      }

      // Calculate distances for all venues from hotel
      const venuesWithHotelDistance = await Promise.all(
        venues.map(async (venue) => {
          if (!venue.latitude || !venue.longitude) {
            return { ...venue, hotelDistanceInfo: null };
          }

          const distanceInfo = await this.calculateDistanceAndTime(
            { latitude: hotelCoords.latitude, longitude: hotelCoords.longitude },
            { latitude: venue.latitude, longitude: venue.longitude }
          );

          return {
            ...venue,
            hotelDistanceInfo: distanceInfo ? {
              ...distanceInfo,
              hotelName: hotelCoords.hotelName || 'Team Hotel'
            } : null
          };
        })
      );

      return venuesWithHotelDistance;

    } catch (error) {
      log.error('Error calculating distances from hotel to venues:', error);
      return venues.map(venue => ({ ...venue, hotelDistanceInfo: null }));
    }
  },

  // Helper function to get invite code ID
  async getInviteCodeId(code) {
    const { data, error } = await supabase
      .from('team_invite_codes')
      .select('id')
      .eq('code', code)
      .maybeSingle();
    
    if (error) {
      log.error('Error getting invite code ID:', error);
      return null;
    }
    
    return data?.id;
  },

  // Registration Functions
  async registerUserWithInvite(userData) {
    try {
      const { email, password, firstName, lastName, phone, inviteCode } = userData;
      
      log.info('📧 Registering user with invite code via edge function:', inviteCode);

      // Use the register-user-with-invite edge function for proper handling
      const response = await supabase.functions.invoke('register-user-with-invite', {
        body: {
          email,
          password,
          firstName,
          lastName,
          phone: phone || null,
          inviteCode: inviteCode.trim().toUpperCase()
        }
      });

      let { data, error } = response;

      // Handle both HTTP errors and application errors
      if (error) {
        log.error('❌ Edge function HTTP error:', error);
        log.error('❌ Full response object keys:', Object.keys(response));
        log.error('❌ Response.error:', response.error);
        log.error('❌ Response.data:', response.data);

        // The Supabase client doesn't expose the raw Response object when using invoke()
        // Instead, we need to use fetch directly to get the error response body
        // Let's try making a direct fetch call to get the error details
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
          const functionUrl = `${supabaseUrl}/functions/v1/register-user-with-invite`;

          log.info('🔄 Making direct fetch to get error details...');
          const fetchResponse = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${supabaseKey}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email,
              password,
              firstName,
              lastName,
              phone: phone || null,
              inviteCode: inviteCode.trim().toUpperCase()
            })
          });

          if (!fetchResponse.ok) {
            const errorBody = await fetchResponse.json();
            log.info('📄 Error response body:', errorBody);
            const errorMessage = errorBody?.error || 'Registration failed';
            throw new Error(errorMessage);
          }

          // If we got here, the fetch succeeded (shouldn't happen since invoke failed)
          data = await fetchResponse.json();
        } catch (fetchError) {
          log.error('❌ Fetch error:', fetchError);
          // If it's our thrown error with the message, rethrow it
          if (fetchError.message && fetchError.message !== 'Failed to fetch') {
            throw fetchError;
          }
          // Otherwise fall back to generic message
          throw new Error(error.message || 'Registration failed');
        }
      }

      if (!data || !data.success) {
        log.error('❌ Registration failed:', data?.error);
        throw new Error(data?.error || 'Registration failed');
      }

      log.info('✅ User registered with invite successfully via edge function');
      
      return { 
        data: { 
          user: data.user,
          needsEmailConfirmation: false,
          message: data.message || 'Registration successful! You have been automatically added to the team.'
        }, 
        error: null 
      };
    } catch (error) {
      log.error('❌ Error registering user with invite:', error);
      return { data: null, error };
    }
  },

  async registerUserWithTeam(userData) {
    try {
      const { email, password, firstName, lastName, phone, selectedTeamId } = userData;
      
      // Verify the team exists
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('id, name')
        .eq('id', selectedTeamId)
        .single();

      if (teamError || !teamData) {
        throw new Error('Selected team not found');
      }

      log.info('📧 Registering user with team selection via edge function for team:', selectedTeamId);

      // Use the register-user-with-team edge function for proper handling
      const response = await supabase.functions.invoke('register-user-with-team', {
        body: {
          email,
          password,
          firstName,
          lastName,
          phone,
          selectedTeamId
        }
      });

      let { data, error } = response;

      // Handle both HTTP errors and application errors
      if (error) {
        log.error('❌ Edge function HTTP error:', error);
        log.error('❌ Full response object keys:', Object.keys(response));
        log.error('❌ Response.error:', response.error);
        log.error('❌ Response.data:', response.data);

        // The Supabase client doesn't expose the raw Response object when using invoke()
        // Instead, we need to use fetch directly to get the error response body
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
          const functionUrl = `${supabaseUrl}/functions/v1/register-user-with-team`;

          log.info('🔄 Making direct fetch to get error details...');
          const fetchResponse = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${supabaseKey}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email,
              password,
              firstName,
              lastName,
              phone,
              selectedTeamId
            })
          });

          if (!fetchResponse.ok) {
            const errorBody = await fetchResponse.json();
            log.info('📄 Error response body:', errorBody);
            const errorMessage = errorBody?.error || 'Registration failed';
            throw new Error(errorMessage);
          }

          // If we got here, the fetch succeeded (shouldn't happen since invoke failed)
          data = await fetchResponse.json();
        } catch (fetchError) {
          log.error('❌ Fetch error:', fetchError);
          // If it's our thrown error with the message, rethrow it
          if (fetchError.message && fetchError.message !== 'Failed to fetch') {
            throw fetchError;
          }
          // Otherwise fall back to generic message
          throw new Error(error.message || 'Registration failed');
        }
      }

      if (!data || !data.success) {
        log.error('❌ Registration failed:', data?.error);
        throw new Error(data?.error || 'Registration failed');
      }

      log.info('✅ User registered with team successfully via edge function');
      
      return { 
        data: { 
          user: data.user,
          pending_request: data.pendingData,
          needsEmailConfirmation: true, // Team selection users need email verification
          message: data.message || 'Registration successful! Please check your email for verification instructions.'
        }, 
        error: null 
      };
    } catch (error) {
      log.error('❌ Error registering user with team:', error);
      return { data: null, error };
    }
  },

  async validateInviteCode(inviteCode) {
    try {
      // Create a temporary client without any authentication for this call
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      // Create a fresh client instance just for this unauthenticated call
      const { createClient } = await import('@supabase/supabase-js');
      const tempClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false
        }
      });

      const { data, error } = await tempClient.functions.invoke('validate-invite-code', {
        body: { inviteCode: inviteCode.trim().toUpperCase() }
      });

      if (error) {
        log.error('❌ Validate invite code error:', error);
        throw new Error(error.message || 'Failed to validate invite code');
      }

      log.info('✅ Invite code validation result:', data);
      
      return { 
        data: {
          is_valid: data.is_valid,
          data: data.data,
          error_message: data.error_message
        }, 
        error: null 
      };
    } catch (error) {
      log.error('❌ Error validating invite code:', error);
      return { data: null, error };
    }
  },

  // User Favorites
  async addVenueToFavorites(venueId, userId = null) {
    const user_id = userId || (await auth.getCurrentUser())?.id;
    if (!user_id) {
      throw new Error('User must be authenticated to add favorites');
    }

    try {
      const { data, error } = await supabase
        .from('user_favorites')
        .insert([{
          user_id: user_id,
          venue_id: venueId
        }])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      log.error('❌ Error adding venue to favorites:', error);
      return { data: null, error };
    }
  },

  async removeVenueFromFavorites(venueId, userId = null) {
    const user_id = userId || (await auth.getCurrentUser())?.id;
    if (!user_id) {
      throw new Error('User must be authenticated to remove favorites');
    }

    try {
      const { data, error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('user_id', user_id)
        .eq('venue_id', venueId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      log.error('❌ Error removing venue from favorites:', error);
      return { data: null, error };
    }
  },

  async isVenueFavorited(venueId, userId = null) {
    const user_id = userId || (await auth.getCurrentUser())?.id;
    if (!user_id) return false;

    try {
      const { data, error } = await supabase
        .rpc('is_venue_favorited', { 
          venue_uuid: venueId, 
          user_uuid: user_id 
        });

      if (error) throw error;
      return data || false;
    } catch (error) {
      log.error('❌ Error checking if venue is favorited:', error);
      return false;
    }
  },

  async getUserFavoriteVenues(userId = null) {
    const user_id = userId || (await auth.getCurrentUser())?.id;
    if (!user_id) {
      return { data: [], error: null };
    }

    try {
      const { data, error } = await supabase
        .rpc('get_user_favorite_venues', { user_uuid: user_id });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      log.error('❌ Error getting user favorite venues:', error);
      return { data: [], error };
    }
  },

  async toggleVenueFavorite(venueId, userId = null) {
    try {
      const isFavorited = await this.isVenueFavorited(venueId, userId);
      
      if (isFavorited) {
        return await this.removeVenueFromFavorites(venueId, userId);
      } else {
        return await this.addVenueToFavorites(venueId, userId);
      }
    } catch (error) {
      log.error('❌ Error toggling venue favorite:', error);
      return { data: null, error };
    }
  },

  // Venue Cuisines
  async getCuisines() {
    try {
      const { data, error } = await supabase
        .rpc('get_cuisines_by_category');

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      log.error('❌ Error getting cuisines:', error);
      return { data: [], error };
    }
  },

  async getCuisineCategories() {
    try {
      const { data, error } = await supabase
        .rpc('get_cuisine_categories');

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      log.error('❌ Error getting cuisine categories:', error);
      return { data: [], error };
    }
  },

  async getCuisineById(cuisineId) {
    try {
      const { data, error } = await supabase
        .from('venue_cuisines')
        .select('*')
        .eq('id', cuisineId)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      log.error('❌ Error getting cuisine by ID:', error);
      return { data: null, error };
    }
  }
}

// Authentication helpers
export const auth = {
  async signUp(email, password, userData = {}) {
    return supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
  },

  async signIn(email, password) {
    return supabase.auth.signInWithPassword({
      email,
      password
    })
  },

  async signOut() {
    return supabase.auth.signOut()
  },

  async signInWithGoogle() {
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        },
        scopes: 'openid email profile'
      }
    })
  },

  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser()
    return user
  },

  async getCurrentSession() {
    const { data: { session } } = await supabase.auth.getSession()
    return session
  },

  async resetPasswordForEmail(email) {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/#reset-password`
    })
  },

  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Real-time subscriptions
export const subscriptions = {
  subscribeToReservations(teamId, callback) {
    return supabase
      .channel('reservations')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'reservations',
        filter: `team_id=eq.${teamId}`
      }, callback)
      .subscribe()
  },

  subscribeToTeamMembers(teamId, callback) {
    return supabase
      .channel('team_members')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'team_members',
        filter: `team_id=eq.${teamId}`
      }, callback)
      .subscribe()
  },

  unsubscribe(subscription) {
    return supabase.removeChannel(subscription)
  },

  async processGeocodingJobs(options = {}) {
    const { limit = 10, max_attempts = 3 } = options;
    
    return supabase.functions.invoke('process-geocoding-jobs', {
      body: { limit, max_attempts }
    });
  }
}
